/**
 * SearchFilter — inline search bar + filter drawer
 *
 * Layout:
 *   [ 🔍 Quick search...  ] [ ⚙ Filters (n) ]
 *   [ Column: value ×  ] [ Column: value × ]  [ Clear all ]
 *
 * "Quick search" searches columns[0] (the primary identifier column).
 * "Filters" button shows active filter count badge and opens a Drawer:
 *   - Desktop (sm+): slides from the right
 *   - Mobile (xs): slides from the bottom with rounded top corners
 *   - Each filter column has a labeled input (text or Yes/No/Any select)
 *   - Footer: "Apply Filters" + "Clear All" buttons
 * Active filters appear as chips that can be individually removed.
 * All filters sync to URL params: filter_<columnName>=<encodedValue>
 */

import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import SearchInput from '@components/Common/SearchInput';
import { useSearchParams } from 'react-router-dom';
import './SearchFilter.scss';

export interface Column {
  id: number;
  columnName: string;
  searchValue: string;
}

interface SearchProps {
  label: string;
  columns: string[];
  setFilterData: React.Dispatch<React.SetStateAction<Column[]>>;
  booleanColumns?: string[];
  /**
   * Columns whose value is a number bound (years, days, pay). They get a numeric keyboard, and
   * anything that is not a digit is dropped as it is typed: the backend ignores a non-numeric
   * bound, so "5 yrs" would otherwise apply no filter while looking like one.
   */
  numberColumns?: string[];
  locationColumns?: string[];
  localityColumns?: string[];
  columnOptions?: Record<string, string[]>;
  /**
   * Display labels for option values, per column.
   *
   * For values that are not words: isPremium's "true"/"false" reading as Managed/Regular. The
   * value sent is still the stored one -- only the chip text changes. Keeping those separate is
   * what stops a repeat of the Subscriptions filter, which offered "Pay Per Job" as the value
   * against a stored PAY_PER_JOB and so matched nothing.
   */
  optionLabels?: Record<string, Record<string, string>>;
  initialColumns?: Column[];
  onApply?: (filters: Column[], clearDates?: boolean) => void;
  /** When true, renders as a persistent vertical panel (for desktop LHS sidebar). */
  sidebarMode?: boolean;
  /** Maps column names to their location API type (e.g. { 'Preferred City': 'city' }) */
  locationColumnTypeMapping?: Record<string, string>;
  /**
   * When a locality is selected, auto-fill the given city/state/country columns.
   * Key = locality column name, value = target column names.
   * e.g. { Locality: { city: 'City', state: 'State', country: 'Country' } }
   */
  localityAutoFillColumns?: Record<string, { city?: string; state?: string; country?: string }>;
  /** Optional content rendered between the filter fields and the Apply button, in both sidebar and drawer mode. */
  extraContent?: React.ReactNode;
  /** Optional content rendered ABOVE the filter columns (top of the panel), in both modes. */
  topContent?: React.ReactNode;
  /**
   * Columns that fetch their options from the server as the user types (server-side search),
   * instead of preloading every option. Key = column name, value = an async search function.
   * Use this for high-cardinality columns (e.g. Company Name) that would otherwise load thousands
   * of options client-side.
   */
  asyncSearch?: Record<string, (query: string) => Promise<string[]>>;
}

const SearchFilter: React.FC<SearchProps> = ({
  columns,
  setFilterData,
  booleanColumns = [],
  numberColumns = [],
  locationColumns = [],
  localityColumns = [],
  columnOptions = {},
  optionLabels = {},
  initialColumns,
  onApply,
  sidebarMode = false,
  locationColumnTypeMapping = {},
  localityAutoFillColumns = {},
  extraContent,
  topContent,
  asyncSearch = {},
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  // Phone tier (< 768px) — the filter panel becomes a bottom sheet with a
  // sticky Apply, rather than a right-side drawer that squeezes the form.
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 768px

  // All active filter cards (what's been applied). Pre-seeded from URL params on mount so that
  // the isSame() check in URL hydration returns true immediately, preventing a redundant
  // setFilterData call (and its cascading second server-side fetch / loading-state flash).
  const [activeFilters, setActiveFilters] = useState<Column[]>(() => {
    const initial: Column[] = [];
    searchParams.forEach((value, key) => {
      if (key.startsWith('filter_') && value.trim() !== '') {
        const col = key.replace('filter_', '');
        if (columns.includes(col)) {
          initial.push({ id: initial.length + 1, columnName: col, searchValue: decodeURIComponent(value) });
        }
      }
    });
    return initial;
  });
  const didHydrateInitial = useRef(false);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Draft filter values inside the drawer (keyed by column name)
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});

  // Quick search (searches columns[0])
  const quickSearchColumn = columns[0] ?? '';

  // Expanded chip groups (for 'show more' in columnOptions chip lists)
  const [expandedChipCols, setExpandedChipCols] = useState<Record<string, boolean>>({});

  // Location autocomplete options per column
  const [locationOpts, setLocationOpts] = useState<Record<string, string[]>>({});
  const locationTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Locality (Google Places sublocality) autocomplete options per column
  const [localityOpts, setLocalityOpts] = useState<Record<string, string[]>>({});
  const localityTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  // placeId lookup: localityPlaceIds.current[col][text] = placeId
  const localityPlaceIds = useRef<Record<string, Record<string, string>>>({});

  // Generic server-side search options per async column
  const [asyncOpts, setAsyncOpts] = useState<Record<string, string[]>>({});
  const asyncTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const fetchAsyncOpts = (col: string, query: string) => {
    clearTimeout(asyncTimers.current[col]);
    if (!query.trim()) {
      setAsyncOpts(prev => ({ ...prev, [col]: [] }));
      return;
    }
    asyncTimers.current[col] = setTimeout(async () => {
      try {
        const results = await asyncSearch[col](query.trim());
        setAsyncOpts(prev => ({ ...prev, [col]: results }));
      } catch {
        setAsyncOpts(prev => ({ ...prev, [col]: [] }));
      }
    }, 350);
  };

  const fetchLocOpts = (col: string, query: string) => {
    clearTimeout(locationTimers.current[col]);
    if (!query.trim()) {
      setLocationOpts(prev => ({ ...prev, [col]: [] }));
      return;
    }
    locationTimers.current[col] = setTimeout(async () => {
      try {
        const res = await axios.post('/api/locationService', {
          query,
          maxCount: 8,
          type: locationColumnTypeMapping[col] ?? col.toLowerCase(),
        });
        const data = res.data as string[] | Array<{ Place?: { Label?: string } }>;
        // Backend returns string[] when type is specified, Place[] otherwise
        const labels: string[] = Array.isArray(data) && typeof data[0] === 'string'
          ? (data as string[])
          : (data as Array<{ Place?: { Label?: string } }>)
              .map(r => r.Place?.Label)
              .filter((l): l is string => !!l);
        setLocationOpts(prev => ({ ...prev, [col]: labels }));
      } catch {
        setLocationOpts(prev => ({ ...prev, [col]: [] }));
      }
    }, 350);
  };

  const fetchLocalityOpts = (col: string, query: string) => {
    clearTimeout(localityTimers.current[col]);
    if (!query.trim()) {
      setLocalityOpts(prev => ({ ...prev, [col]: [] }));
      return;
    }
    localityTimers.current[col] = setTimeout(async () => {
      try {
        const res = await axios.post('/api/localityService', {
          query,
          maxCount: 8,
        });
        const data = res.data as Array<{ text: string; placeId: string }>;
        // Cache placeId for each result so we can look it up on selection
        if (!localityPlaceIds.current[col]) localityPlaceIds.current[col] = {};
        data.forEach(r => { if (r.text && r.placeId) localityPlaceIds.current[col][r.text] = r.placeId; });
        const labels = data.map(r => r.text).filter(Boolean);
        setLocalityOpts(prev => ({ ...prev, [col]: labels }));
      } catch {
        setLocalityOpts(prev => ({ ...prev, [col]: [] }));
      }
    }, 350);
  };
  const [quickSearchValue, setQuickSearchValue] = useState('');

  // Columns that appear in the drawer (all except quick-search column)
  const drawerColumns = columns.filter(c => c !== quickSearchColumn);

  // Active non-quick-search filters (shown as chips + badge count)
  const chipFilters = activeFilters.filter(f => f.columnName !== quickSearchColumn);

  // ──────────────────────────────────
  // Sidebar draft seeding (desktop mode)
  // Re-seed inputs whenever active filters (URL) change
  // ──────────────────────────────────
  useEffect(() => {
    if (!sidebarMode) return;
    const draft: Record<string, string> = {};
    columns.forEach(col => {
      draft[col] = activeFilters.find(f => f.columnName === col)?.searchValue ?? '';
    });
    setDraftValues(draft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarMode, activeFilters]);

  // ──────────────────────────────────
  // URL hydration (back/forward support)
  // ──────────────────────────────────
  const isSame = (a: Column[], b: Column[]) => {
    if (a.length !== b.length) return false;
    return a.every((f, i) => f.columnName === b[i].columnName && f.searchValue === b[i].searchValue);
  };

  useEffect(() => {
    const urlFilters: Column[] = [];
    searchParams.forEach((value, key) => {
      if (key.startsWith('filter_') && value.trim() !== '') {
        const col = key.replace('filter_', '');
        if (columns.includes(col)) {
          urlFilters.push({ id: urlFilters.length + 1, columnName: col, searchValue: decodeURIComponent(value) });
        }
      }
    });

    if (urlFilters.length > 0) {
      if (!isSame(activeFilters, urlFilters)) {
        setActiveFilters(urlFilters);
        setFilterData(urlFilters);
        const qf = urlFilters.find(f => f.columnName === quickSearchColumn);
        setQuickSearchValue(qf?.searchValue ?? '');
      }
      didHydrateInitial.current = true;
    } else {
      if (!didHydrateInitial.current) {
        if (initialColumns && initialColumns.length > 0) {
          if (!isSame(activeFilters, initialColumns)) {
            setActiveFilters(initialColumns);
            setFilterData(initialColumns);
            const qf = initialColumns.find(f => f.columnName === quickSearchColumn);
            setQuickSearchValue(qf?.searchValue ?? '');
          }
        }
        didHydrateInitial.current = true;
      } else if (activeFilters.length > 0) {
        setActiveFilters([]);
        setFilterData([]);
        setQuickSearchValue('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  // ──────────────────────────────────
  // Apply / remove helpers
  // ──────────────────────────────────
  const applyFilters = useCallback((filters: Column[], clearDates = false) => {
    const newParams = new URLSearchParams(searchParams);
    Array.from(newParams.keys())
      .filter(k => k.startsWith('filter_'))
      .forEach(k => newParams.delete(k));
    if (clearDates) {
      newParams.delete('date_from');
      newParams.delete('date_to');
    }
    filters.forEach(f => newParams.set(`filter_${f.columnName}`, encodeURIComponent(f.searchValue)));
    // Reset pagination when filters change — prevents competing setSearchParams in page components
    newParams.delete('page');
    if (newParams.toString() !== searchParams.toString()) setSearchParams(newParams);
    setActiveFilters(filters);
    setFilterData(filters);
    onApply?.(filters, clearDates);
  }, [searchParams, setSearchParams, setFilterData, onApply]);

  const removeFilter = useCallback((columnName: string) => {
    const updated = activeFilters.filter(f => f.columnName !== columnName);
    if (columnName === quickSearchColumn) setQuickSearchValue('');
    applyFilters(updated);
  }, [activeFilters, quickSearchColumn, applyFilters]);

  const clearAll = useCallback(() => {
    setQuickSearchValue('');
    applyFilters([], true);
  }, [applyFilters]);

  const addOrUpdateFilter = useCallback((columnName: string, value: string) => {
    if (!value.trim()) return;
    const existing = activeFilters.filter(f => f.columnName !== columnName);
    const updated: Column[] = [
      ...existing,
      { id: existing.length + 1, columnName, searchValue: value.trim() },
    ];
    applyFilters(updated);
  }, [activeFilters, applyFilters]);

  // ──────────────────────────────────
  // Sidebar apply (desktop mode)
  // ──────────────────────────────────
  /**
   * @param overrides values to use INSTEAD of what is in `draftValues` for those columns.
   *
   * A chip that applies on click cannot rely on `draftValues`: it calls setDraftValues and then
   * commits in the same handler, and the state has not flushed yet, so the commit would use the
   * previous selection. Passing the new value through explicitly is what makes one click do one
   * thing. The Apply button passes nothing and reads state as before.
   */
  const applySidebarFilters = useCallback((overrides?: Record<string, string>) => {
    const values = { ...draftValues, ...(overrides ?? {}) };
    const allCols = [quickSearchColumn, ...drawerColumns];
    const newFilters: Column[] = [];
    allCols.forEach(col => {
      const val = values[col]?.trim();
      if (val) newFilters.push({ id: newFilters.length + 1, columnName: col, searchValue: val });
    });
    setQuickSearchValue(values[quickSearchColumn]?.trim() ?? '');
    applyFilters(newFilters);
  }, [quickSearchColumn, drawerColumns, draftValues, applyFilters]);

  // ──────────────────────────────────
  // Quick search (columns[0])
  // ──────────────────────────────────
  const handleQuickSearch = () => {
    if (quickSearchValue.trim()) {
      addOrUpdateFilter(quickSearchColumn, quickSearchValue);
    } else {
      removeFilter(quickSearchColumn);
    }
  };

  const handleQuickSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleQuickSearch();
    if (e.key === 'Escape') {
      setQuickSearchValue('');
      removeFilter(quickSearchColumn);
    }
  };

  // ──────────────────────────────────
  // Drawer
  // ──────────────────────────────────
  const openDrawer = () => {
    // Seed draft values from currently active filters
    const draft: Record<string, string> = {};
    drawerColumns.forEach(col => {
      draft[col] = activeFilters.find(f => f.columnName === col)?.searchValue ?? '';
    });
    setDraftValues(draft);
    setDrawerOpen(true);
  };

  const closeDrawer = () => setDrawerOpen(false);

  /**
   * @param overrides see applySidebarFilters — a chip commits its own new value.
   * @param keepOpen  true when a chip applied itself, so several can be picked in one visit.
   *                  The Apply button still closes, because pressing it means "done".
   */
  const applyDrawerFilters = (
    overrides?: Record<string, string>,
    keepOpen = false,
  ) => {
    const values = { ...draftValues, ...(overrides ?? {}) };
    // Preserve quick-search filter, replace all drawer-column filters
    const quickFilter = activeFilters.filter(f => f.columnName === quickSearchColumn);
    const drawerFilters: Column[] = [];
    drawerColumns.forEach(col => {
      const val = values[col]?.trim();
      if (val) drawerFilters.push({ id: drawerFilters.length + 1, columnName: col, searchValue: val });
    });
    applyFilters([...quickFilter, ...drawerFilters]);
    if (!keepOpen) closeDrawer();
  };

  const clearDrawerAndAll = () => {
    setDraftValues({});
    setQuickSearchValue('');
    applyFilters([], true);
    closeDrawer();
  };

  // ── Sidebar mode render (desktop LHS panel) ─────────────────
  /**
   * An enum value as a person would read it, without changing the value itself.
   *
   * It was `opt.charAt(0).toUpperCase() + opt.slice(1).toLowerCase()`, which rendered
   * CONTRACT_SENT as "Contract_sent" and PAY_PER_JOB as "Pay per job" -- the underscore survives
   * because only the first character was touched.
   *
   * Display only. The stored value is what gets filtered on, and collapsing the two is exactly
   * how the Subscriptions filter came to offer "Pay Per Job" against a stored PAY_PER_JOB.
   */
  const humaniseOption = (opt: string): string => {
    const words = opt.replace(/_/g, ' ').trim().toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
  };

  /** Number of chips visible before the 'Show more' control appears. */
  const CHIP_VISIBLE_LIMIT = 6;
  /** If columnOptions has more entries than this threshold, render an Autocomplete instead of chips. */
  const AUTOCOMPLETE_THRESHOLD = 15;

  /**
   * Renders the appropriate input control for a given filter column.
   *
   * @param onCommit applies the filters. Typed fields call it on Enter; option chips call it on
   *   click, passing the value they just set — see the note on applySidebarFilters for why the
   *   value has to travel with the call rather than be read back from state.
   */
  const renderField = (
    col: string,
    onCommit?: (_overrides?: Record<string, string>) => void,
  ) => {
    const opts = columnOptions[col];
    const isBoolean = booleanColumns.includes(col);
    const isLocation = locationColumns.includes(col);
    const isLocality = localityColumns.includes(col);
    const isAsync = !!asyncSearch[col];

    // Server-side search (high-cardinality columns, e.g. Company Name) — type to fetch matches.
    if (isAsync) {
      return (
        <Autocomplete
          freeSolo
          fullWidth
          options={asyncOpts[col] ?? []}
          value={draftValues[col] ?? ''}
          className="sf-location-autocomplete"
          filterOptions={(x) => x}
          onChange={(_, val) => setDraftValues(prev => ({ ...prev, [col]: (val as string) ?? '' }))}
          onInputChange={(_, val) => {
            setDraftValues(prev => ({ ...prev, [col]: val }));
            fetchAsyncOpts(col, val);
          }}
          renderInput={params => (
            <TextField {...params} placeholder={`Search ${col}…`} size="small" variant="outlined" />
          )}
        />
      );
    }

    // Predefined options — Autocomplete for large lists, chips for small lists
    if (opts) {
      // Large list → Autocomplete dropdown
      if (opts.length > AUTOCOMPLETE_THRESHOLD) {
        return (
          <Autocomplete
            fullWidth
            options={opts}
            value={draftValues[col] || null}
            className="sf-location-autocomplete"
            onChange={(_, val) => {
              setDraftValues(prev => ({ ...prev, [col]: val ?? '' }));
            }}
            renderInput={params => (
              <TextField
                {...params}
                placeholder={`Search ${col}…`}
                size="small"
                variant="outlined"
              />
            )}
          />
        );
      }

      // Small/medium list → chips with optional 'Show more'
      const isExpanded = expandedChipCols[col] ?? false;
      const visibleOpts = isExpanded ? opts : opts.slice(0, CHIP_VISIBLE_LIMIT);
      const hiddenCount = opts.length - CHIP_VISIBLE_LIMIT;
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, pt: 0.25 }}>
          {visibleOpts.map(opt => {
            const active = draftValues[col] === opt;
            return (
              <Chip
                key={opt}
                clickable
                color={active ? 'primary' : 'default'}
                label={optionLabels[col]?.[opt] ?? humaniseOption(opt)}
                size="small"
                variant={active ? 'filled' : 'outlined'}
                // The global MuiChip root override pins `color: var(--tx-mid)`, which otherwise
                // wins over the filled-primary contrast text and leaves a selected chip's label
                // mid-grey on the brand fill -- unreadable. sx is the one layer above it, so the
                // selected chip states its own AAA pair (white on brand) explicitly.
                sx={active ? { bgcolor: 'var(--brand)', color: 'var(--on-brand)' } : undefined}
                onClick={() => {
                  /*
                   * Applies immediately. A chip is an "apply now" affordance, and this used to
                   * only tick it -- the list did not move until a separate Apply Filters press,
                   * which reads as a dead control. That is what was reported as "the buttons
                   * aren't clickable, on clicking it nothing happens": the chip did highlight,
                   * and then nothing else did.
                   */
                  const next = active ? '' : opt;
                  setDraftValues(prev => ({ ...prev, [col]: next }));
                  onCommit?.({ [col]: next });
                }}
              />
            );
          })}
          {hiddenCount > 0 && (
            <Chip
              size="small"
              label={isExpanded ? 'Show less' : `+${hiddenCount} more`}
              sx={{
                fontSize: 'var(--font-size-xs)',
                height: 26,
                bgcolor: 'action.hover',
                cursor: 'pointer',
                fontWeight: 500,
                '&:hover': { bgcolor: 'action.selected' },
              }}
              onClick={() => setExpandedChipCols(prev => ({ ...prev, [col]: !isExpanded }))}
            />
          )}
        </Box>
      );
    }

    // Boolean chip toggle (Any / Yes / No)
    if (isBoolean) {
      return (
        <Box sx={{ display: 'flex', gap: 0.75, pt: 0.25 }}>
          {(['', 'true', 'false'] as const).map((val, i) => {
            const label = i === 0 ? 'Any' : i === 1 ? 'Yes' : 'No';
            const active = (draftValues[col] ?? '') === val;
            return (
              <Chip
                key={val}
                clickable
                color={active ? 'primary' : 'default'}
                label={label}
                size="small"
                variant={active ? 'filled' : 'outlined'}
                // Same contrast fix as the option chips above: sx sits above the MuiChip root
                // override, so the selected boolean chip is white on brand rather than mid-grey.
                sx={active ? { bgcolor: 'var(--brand)', color: 'var(--on-brand)' } : undefined}
                onClick={() => {
                  // Same as the option chips above: a toggle that does not toggle anything reads
                  // as broken.
                  setDraftValues(prev => ({ ...prev, [col]: val }));
                  onCommit?.({ [col]: val });
                }}
              />
            );
          })}
        </Box>
      );
    }

    // Location autocomplete
    if (isLocation) {
      return (
        <Autocomplete
          freeSolo
          fullWidth
          options={locationOpts[col] ?? []}
          value={draftValues[col] ?? ''}
          className="sf-location-autocomplete"
          onChange={(_, val) => {
            setDraftValues(prev => ({ ...prev, [col]: (val as string) ?? '' }));
          }}
          onInputChange={(_, val) => {
            setDraftValues(prev => ({ ...prev, [col]: val }));
            fetchLocOpts(col, val);
          }}
          renderInput={params => (
            <TextField
              {...params}
              placeholder="Search location…"
              size="small"
              variant="outlined"
            />
          )}
        />
      );
    }

    // Locality (neighbourhood) autocomplete via Google Places
    if (isLocality) {
      const autoFill = localityAutoFillColumns[col];

      const handleLocalityChange = (_: React.SyntheticEvent, val: string | null) => {
        const text = val ?? '';
        // Show full text immediately so the input isn't blank while we fetch
        setDraftValues(prev => ({ ...prev, [col]: text }));
        if (!text) return;
        const placeId = localityPlaceIds.current[col]?.[text];
        if (!placeId) return;
        axios.get<{ locality?: string; city?: string; state?: string; country?: string }>(`/api/placeDetails?placeId=${placeId}`)
          .then(res => {
            const { locality, city, state, country } = res.data;
            // Replace the full "Bandra West, Mumbai, Maharashtra, India" with just "Bandra West"
            const shortLocality = locality || text.split(',')[0].trim();
            setDraftValues(prev => ({
              ...prev,
              [col]: shortLocality,
              ...(autoFill?.city && city ? { [autoFill.city]: city } : {}),
              ...(autoFill?.state && state ? { [autoFill.state]: state } : {}),
              ...(autoFill?.country && country ? { [autoFill.country]: country } : {}),
            }));
          })
          .catch(() => {});
      };

      return (
        <Autocomplete
          freeSolo
          fullWidth
          options={localityOpts[col] ?? []}
          value={draftValues[col] ?? ''}
          className="sf-location-autocomplete"
          onChange={handleLocalityChange}
          onInputChange={(_, val) => {
            setDraftValues(prev => ({ ...prev, [col]: val }));
            fetchLocalityOpts(col, val);
          }}
          renderInput={params => (
            <TextField
              {...params}
              placeholder="Search neighbourhood…"
              size="small"
              variant="outlined"
            />
          )}
        />
      );
    }

    if (numberColumns.includes(col)) {
      return (
        <SearchInput
          icon={false}
          inputMode="numeric"
          placeholder="Number, e.g. 5"
          ariaLabel={col}
          value={draftValues[col] ?? ''}
          onChange={v => setDraftValues(prev => ({ ...prev, [col]: v.replace(/[^\d]/g, '') }))}
          onKeyDown={e => { if (e.key === 'Enter') onCommit?.(); }}
        />
      );
    }

    // Default text input — outlined field, consistent with the autocomplete controls above.
    return (
      <SearchInput
        icon={false}
        placeholder={`Filter by ${col}…`}
        value={draftValues[col] ?? ''}
        onChange={v => setDraftValues(prev => ({ ...prev, [col]: v }))}
        onKeyDown={e => { if (e.key === 'Enter') onCommit?.(); }}
      />
    );
  };

  if (sidebarMode) {
    const allCols = [quickSearchColumn, ...drawerColumns];
    return (
      <Box className="sf-sidebar">
        {/* Header */}
        <Box className="sf-sidebar-header">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <FilterListIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Filters
            </Typography>
          </Box>
          {chipFilters.length > 0 && (
            <Box
              className="sf-clear-all"
              role="button"
              tabIndex={0}
              onClick={clearAll}
              onKeyDown={e => { if (e.key === 'Enter') clearAll(); }}
            >
              Clear all
            </Box>
          )}
        </Box>

        {/* Filter fields */}
        <Box className="sf-sidebar-body">
          {topContent}
          {allCols.map(col => (
            <Box key={col} className="sf-drawer-field">
              <Typography className="sf-drawer-field-label" variant="overline">
                {col}
              </Typography>
              {renderField(col, applySidebarFilters)}
            </Box>
          ))}
          {/* Inside the scroller, so the date range and the page's own control are part of the
              same list rather than a block pinned beneath it. */}
          {extraContent}
        </Box>

        {/* Footer */}
        <Box className="sf-sidebar-footer">
          <Button fullWidth size="small" variant="contained" onClick={() => applySidebarFilters()}>
            Apply Filters
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="sf-root">
      {/* ── Row 1: search input + filter button ── */}
      <Box className="sf-bar">
        {/* Quick search input */}
        <Box className="sf-search-box">
          <SearchInput
            placeholder={`Search by ${quickSearchColumn}…`}
            value={quickSearchValue}
            onBlur={handleQuickSearch}
            onChange={setQuickSearchValue}
            onKeyDown={handleQuickSearchKeyDown}
            onClear={() => { setQuickSearchValue(''); removeFilter(quickSearchColumn); }}
          />
        </Box>

        {/* Filter button with badge */}
        {drawerColumns.length > 0 && (
          <Badge
            badgeContent={chipFilters.length || null}
            color="primary"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: 'var(--font-size-3xs)',
                height: 16,
                minWidth: 16,
                right: 3,
                top: 3,
                fontWeight: 700,
              },
            }}
          >
            <Box
              aria-label="Open filters"
              className={`sf-filter-btn${drawerOpen ? ' open' : ''}${chipFilters.length > 0 ? ' has-active' : ''}`}
              role="button"
              tabIndex={0}
              onClick={openDrawer}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') openDrawer(); }}
            >
              <FilterListIcon className="sf-filter-icon" />
              <span className="sf-filter-label">Filters</span>
            </Box>
          </Badge>
        )}
      </Box>

      {/* ── Row 2: active filter chips ── */}
      {chipFilters.length > 0 && (
        <Box className="sf-chips">
          {chipFilters.map(f => (
            <Chip
              key={f.columnName}
              className="sf-chip"
              label={`${f.columnName}: ${f.searchValue}`}
              size="small"
              onDelete={() => removeFilter(f.columnName)}
            />
          ))}
          <Box
            className="sf-clear-all"
            role="button"
            tabIndex={0}
            onClick={clearAll}
            onKeyDown={e => { if (e.key === 'Enter') clearAll(); }}
          >
            Clear all
          </Box>
        </Box>
      )}

      {/* ── Filter Drawer ── */}
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={drawerOpen}
        slotProps={{
          paper: {
            className: 'sf-drawer-paper',
            sx: isMobile
              ? { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '85vh' }
              : { width: 320 },
          },
        }}
        onClose={closeDrawer}
      >
        {/* Drawer header */}
        <Box className="sf-drawer-header">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Filters
            </Typography>
            {chipFilters.length > 0 && (
              <Chip
                color="primary"
                label={chipFilters.length}
                size="small"
                sx={{ height: 18, fontSize: 'var(--font-size-3xs)', fontWeight: 700, '& .MuiChip-label': { px: '6px' } }}
              />
            )}
          </Box>
          <IconButton aria-label="Close filters" onClick={closeDrawer}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Drawer body — one input per filter column */}
        <Box className="sf-drawer-body">
          {topContent}
          {drawerColumns.map(col => (
            <Box key={col} className="sf-drawer-field">
              <Typography className="sf-drawer-field-label" variant="overline">
                {col}
              </Typography>
              {/*
                * Overrides are only passed by a chip. Enter on a typed field commits and closes,
                * as it always did; a chip applies and leaves the drawer up so several can be
                * picked in one visit.
                */}
              {renderField(col, o => applyDrawerFilters(o, !!o))}
            </Box>
          ))}
          {/* Inside the scroller. Rendered after it, this slot sat between the body and the
              footer and took its own height off the list -- on a phone the columns were squeezed
              into a stub while the date range and Recruiter stayed pinned below them. */}
          {extraContent}
        </Box>

        {/* Drawer footer */}
        <Box className="sf-drawer-footer">
          <Button fullWidth variant="contained" onClick={() => applyDrawerFilters()}>
            Apply Filters
          </Button>
          <Button
            fullWidth
            color="inherit"
            variant="outlined"
            onClick={clearDrawerAndAll}
            sx={{ color: 'text.secondary' }}
          >
            Clear All
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
};

export default SearchFilter;
